import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import BusRouteApi from "Services/BusRoute";
import PlaceApi from "Services/Place";

const BusRouteCreateModal = ({ openDialog, handleClose, routeId = "" }) => {
  const dispatch = useDispatch();
  const [places, setPlaces] = useState([]);
  const isEditMode = Boolean(routeId);

  const normalizePlaces = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.places)) return payload.places || [];
    if (Array.isArray(payload?.data?.places)) return payload.data.places || [];
    if (Array.isArray(payload?.data?.data)) return payload.data.data || [];
    if (Array.isArray(payload?.data)) return payload.data || [];
    return [];
  };

  useEffect(() => {
    if (!openDialog) return;
    if (!isEditMode) formik.resetForm();

    PlaceApi.getPlaceList({ page: 1, limit: 1000, search: "" }).then(
      (res) => setPlaces(normalizePlaces(res)),
      () => {
        setPlaces([]);
        dispatch(
          showSnackbar({
            message: "Unable to load places",
            autoHideDuration: 2500,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [openDialog, isEditMode]);

  useEffect(() => {
    if (!openDialog || !isEditMode || !routeId) return;
    dispatch(showLoader("Loading please wait..."));
    BusRouteApi.getBusRouteById(routeId).then(
      (res) => {
        dispatch(hideLoader());
        const data = res?.data || {};
        formik.setValues({
          route_code: data?.route_code || "",
          origin_place_id: data?.origin_place_id || "",
          destination_place_id: data?.destination_place_id || "",
          duration_minutes: Number(data?.duration_minutes ?? 240),
          is_active: Boolean(data?.is_active),
        });
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Unable to load bus route details",
            autoHideDuration: 2500,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [openDialog, isEditMode, routeId, dispatch]);

  const formik = useFormik({
    initialValues: {
      route_code: "",
      origin_place_id: "",
      destination_place_id: "",
      duration_minutes: 240,
      is_active: true,
    },
    validationSchema: yup.object({
      route_code: yup.string().required("Route code is required"),
      origin_place_id: yup.string().required("Origin place is required"),
      destination_place_id: yup.string().required("Destination place is required"),
      duration_minutes: yup.number().required("Duration is required").min(1, "Minimum is 1"),
      is_active: yup.boolean().required("Status is required"),
    }),
    onSubmit: (values) => {
      const payload = {
        route_code: values.route_code,
        origin_place_id: values.origin_place_id,
        destination_place_id: values.destination_place_id,
        duration_minutes: Number(values.duration_minutes),
        is_active: Boolean(values.is_active),
      };

      dispatch(showLoader("Loading please wait..."));
      const request = isEditMode ? BusRouteApi.updateBusRoute(routeId, payload) : BusRouteApi.createBusRoute(payload);
      request.then(
        (res) => {
          dispatch(hideLoader());
          if (res?.status === 200 || res?.status === 201) {
            dispatch(
              showSnackbar({
                message: res?.data?.message || (isEditMode ? "Bus route updated successfully" : "Bus route created successfully"),
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "success",
              })
            );
            handleClose(true);
          }
        },
        (err) => {
          dispatch(hideLoader());
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || (isEditMode ? "Error while updating bus route" : "Error while creating bus route"),
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "error",
            })
          );
        }
      );
    },
  });

  const getPlaceId = (place) => place?.id || place?._id || "";
  const getPlaceLabel = (place) => {
    const name = place?.name || getPlaceId(place) || "-";
    const locality = place?.addressLocality ? ` - ${place.addressLocality}` : "";
    return `${name}${locality}`;
  };

  return (
    <Dialog open={openDialog} onClose={() => handleClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{isEditMode ? "Edit Bus Route" : "Add Bus Route"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Route Code"
              name="route_code"
              value={formik.values.route_code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.route_code && Boolean(formik.errors.route_code)}
              helperText={formik.touched.route_code && formik.errors.route_code}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Origin"
              name="origin_place_id"
              value={formik.values.origin_place_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.origin_place_id && Boolean(formik.errors.origin_place_id)}
              helperText={formik.touched.origin_place_id && formik.errors.origin_place_id}
            >
              <MenuItem value="">Select Origin</MenuItem>
              {places.map((place) => {
                const placeId = getPlaceId(place);
                if (!placeId) return null;
                return (
                  <MenuItem key={placeId} value={placeId}>
                    {getPlaceLabel(place)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Destination"
              name="destination_place_id"
              value={formik.values.destination_place_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.destination_place_id && Boolean(formik.errors.destination_place_id)}
              helperText={formik.touched.destination_place_id && formik.errors.destination_place_id}
            >
              <MenuItem value="">Select Destination</MenuItem>
              {places.map((place) => {
                const placeId = getPlaceId(place);
                if (!placeId) return null;
                return (
                  <MenuItem key={placeId} value={placeId}>
                    {getPlaceLabel(place)}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Duration (minutes)"
              name="duration_minutes"
              value={formik.values.duration_minutes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.duration_minutes && Boolean(formik.errors.duration_minutes)}
              helperText={formik.touched.duration_minutes && formik.errors.duration_minutes}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={Boolean(formik.values.is_active)} onChange={(e) => formik.setFieldValue("is_active", e.target.checked)} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)}>Cancel</Button>
        <Button variant="contained" onClick={formik.handleSubmit}>{isEditMode ? "Update" : "Add"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusRouteCreateModal;
